import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Stack,
  Autocomplete,
  Tooltip,
  IconButton,
  InputAdornment,
  Button,
  Paper,
} from '@mui/material';
import {
  Info as InfoIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { FirmProfile } from '../../types';
import { updateFirmProfile, createFirmProfile } from '../../services/api';

interface FirmProfileViewProps {
  onBack: () => void;
  firmProfile?: FirmProfile | null;
  isPrivilegedUser: boolean;
}

const FirmProfileView: React.FC<FirmProfileViewProps> = ({ onBack, firmProfile: initialFirmProfile, isPrivilegedUser }) => {
  const [firmProfile, setFirmProfile] = useState<FirmProfile | null>(initialFirmProfile || null);
  const [isNew, setIsNew] = useState(!initialFirmProfile);

  useEffect(() => {
    if (!initialFirmProfile) {
      // If no firm profile is passed, create a new one
      setIsNew(true);
      setFirmProfile({
        id: '',
        firm_name: '',
        sector_focus: [],
        industry_focus: [],
        stage_focus: [],
        geographic_focus: [],
        investment_size_min: '0',
        investment_size_max: '0',
        holding_period_target: 0,
        holding_period_unit: 'years',
        exit_strategy_preferences: [],
        target_portfolio_mix: {},
        organization_id: '',
        created_at: '',
        updated_at: '',
      });
    } else {
      setFirmProfile(initialFirmProfile);
      setIsNew(false);
    }
  }, [initialFirmProfile]);

  const handleSave = async () => {
    if (!firmProfile) return;

    try {
      if (isNew) {
        await createFirmProfile(firmProfile);
      } else {
        await updateFirmProfile(firmProfile);
      }
      onBack();
    } catch (error) {
      console.error('Failed to save firm profile:', error);
    }
  };

  const handleChange = (field: keyof FirmProfile, value: any) => {
    if (firmProfile) {
      setFirmProfile({ ...firmProfile, [field]: value });
    }
  };

  if (!firmProfile) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>Firm Profile</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Define your firm's basic investment parameters and strategy focus.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          fullWidth
          label="Firm Name / Fund Name"
          value={firmProfile.firm_name}
          onChange={(e) => handleChange('firm_name', e.target.value)}
          variant="outlined"
          disabled={!isPrivilegedUser}
        />

        <Box>
          <Typography variant="subtitle1" gutterBottom>Sector Focus</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={firmProfile.sector_focus}
              onChange={(event, newValue) => {
                handleChange('sector_focus', newValue);
              }}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Sector Focus"
                  placeholder="Add sectors"
                  disabled={!isPrivilegedUser}
                />
              )}
              disabled={!isPrivilegedUser}
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom>Geographic Focus</Typography>
          <Autocomplete
            multiple
            id="geographic-focus"
            options={['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Australia', 'Middle East']}
            value={firmProfile.geographic_focus}
            onChange={(event, newValue) => {
              handleChange('geographic_focus', newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select regions" disabled={!isPrivilegedUser} />
            )}
            disabled={!isPrivilegedUser}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '250px' }}>
            <Typography variant="subtitle1" gutterBottom>
              Typical Investment Size
              <Tooltip title="The typical equity check size for investments">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <TextField
              fullWidth
              label="Minimum"
              value={firmProfile.investment_size_min}
              onChange={(e) => handleChange('investment_size_min', Number(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              type="number"
              sx={{ mb: 2 }}
              disabled={!isPrivilegedUser}
            />
            <TextField
              fullWidth
              label="Maximum"
              value={firmProfile.investment_size_max}
              onChange={(e) => handleChange('investment_size_max', Number(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              type="number"
              disabled={!isPrivilegedUser}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: '250px' }}>
            <Typography variant="subtitle1" gutterBottom>Holding Period Target</Typography>
            <TextField
              fullWidth
              label="Years"
              value={firmProfile.holding_period_target}
              onChange={(e) => handleChange('holding_period_target', Number(e.target.value))}
              type="number"
              disabled={!isPrivilegedUser}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom>Exit Strategy Preferences</Typography>
          <Autocomplete
            multiple
            id="exit-strategies"
            options={['IPO', 'Strategic Sale', 'Secondary Buyout', 'Recapitalization', 'Management Buyout']}
            value={firmProfile.exit_strategy_preferences}
            onChange={(event, newValue) => {
              handleChange('exit_strategy_preferences', newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select exit strategies" disabled={!isPrivilegedUser} />
            )}
            disabled={!isPrivilegedUser}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
          />
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Target Portfolio Mix (%)
            <Tooltip title="Percentage allocation target for each sector in your portfolio">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* This part needs a more complex implementation to add/remove sectors and update percentages */}
            <Typography color="text.secondary">Portfolio mix editing is not yet implemented.</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        {isPrivilegedUser && (
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ mr: 2 }}>
            Save
          </Button>
        )}
        <Button variant="outlined" startIcon={<CancelIcon />} onClick={onBack}>
          Back
        </Button>
      </Box>
    </Paper>
  );
};

export default FirmProfileView;